import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { debounce } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';

import useHandleError from '@/hooks/useHandleError';
import {
  organizationsAtom,
  selectedOrganizationAtom
} from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';

import type { OrgUser } from '../Administration.types';
import { mapUserDTO, roleTypesAtom } from './Users.store';
import type { User, UserDTO } from './Users.types';

export const useUsers = () => {
  const [organizations] = useAtom(organizationsAtom);
  const [, setRoleTypes] = useAtom(roleTypesAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [search, setSearch] = useState('');
  const [user, setUser] = useState<User>();
  const handleError = useHandleError();

  /**
   * fetch organization users
   */
  const {
    data: users,
    error: errorUsers,
    isError: isErrorUsers,
    isFetching: isFetchingUsers,
    refetch: refetchUsers
  } = useQuery({
    queryKey: [`organizations/${selectedOrganization.label}/users`],
    staleTime: 1000,
    enabled: false
  });

  useEffect(() => {
    if (isErrorUsers) {
      handleError(errorUsers as AxiosError, 'Failure to fetch users.');
    }
  }, [isErrorUsers]);

  /**
   * fetch organization role types
   */
  const {
    data: roleTypes,
    error: errorRoleTypes,
    isError: isErrorRoleTypes,
    isSuccess: isSuccessRoleTypes,
    refetch: refetchRoleTypes
  } = useQuery({
    queryKey: [
      `role_types/${
        selectedOrganization.label === 'all' ? '' : selectedOrganization.label
      }`
    ],
    staleTime: Infinity,
    enabled: false
  });

  useEffect(() => {
    // onSuccess
    if (Array.isArray(roleTypes) && isSuccessRoleTypes) {
      setRoleTypes(roleTypes);
    }

    // onError
    if (isErrorRoleTypes) {
      handleError(errorRoleTypes as AxiosError, 'Failure to fetch roles.');
    }
  }, [roleTypes, isErrorRoleTypes, isSuccessRoleTypes]);

  /**
   * send invitation email
   */
  const {
    data: invitationEmail,
    error: errorSendInvitationEmail,
    isError: isErrorSendInvitationEmail,
    isSuccess: isSuccessSendInvitationEmail,
    refetch: sendInvitationEmail
  } = useQuery({
    queryKey: [`users/send_invite/${selectedOrganization.label}/${user?.id}`],
    staleTime: Infinity,
    enabled: false
  });

  useEffect(() => {
    // onSuccess
    if (invitationEmail && isSuccessSendInvitationEmail) {
      enqueueSnackbar(`Invitation email sent to ${user?.name}.`, {
        variant: 'success'
      });
    }

    // onError
    if (isErrorSendInvitationEmail) {
      handleError(
        errorSendInvitationEmail as AxiosError,
        'Failure to send invitation email.'
      );
    }
  }, [
    isErrorSendInvitationEmail,
    isSuccessSendInvitationEmail,
    invitationEmail
  ]);

  /**
   * reset mfa
   */
  const resetMfa = useMutation({
    mutationFn: () => axiosClient.post(`/users/${user?.id}/reset_mfa`),
    onSuccess: () => {
      enqueueSnackbar(`MFA reset for ${user?.name}`, {
        variant: 'success'
      });
    },
    onError: (error: AxiosError) => {
      handleError(error, `Failure to reset MFA for ${user?.name}`);
    }
  });

  /**
   * fetch users when organizations are loaded
   * ensures that we don't fetch users first for all organizations
   */
  useEffect(() => {
    if (organizations.length > 0) {
      refetchUsers();
      refetchRoleTypes();
    }
  }, [organizations, selectedOrganization.label]);

  return {
    isFetchingUsers,
    refetchUsers,
    resetMfa,
    roleTypes,
    search,
    sendInvitationEmail,
    setSearch: debounce(setSearch, 300),
    setUser,
    user,
    users:
      (users as Array<UserDTO>)?.map((u) =>
        mapUserDTO(u, selectedOrganization.label)
      ) || []
  };
};

export const useFetchOrgUsers = (excludeServiceAccounts: boolean = false) => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();

  const { data, isLoading, isError, error } = useQuery<OrgUser[]>({
    queryKey: [
      `organizations/${selectedOrganization?.label}/users${
        excludeServiceAccounts ? '?exclude_service_accounts=true' : ''
      }`
    ],
    enabled:
      selectedOrganization?.label !== 'all' && !!selectedOrganization?.label,
    staleTime: Infinity
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch organization users.');
    }
  }, [error, isError]);

  return {
    isLoading,
    data: data || []
  };
};

export default useUsers;
